// TicketsKanbanPage.tsx
// /tickets surface — replaces the stacked-card TicketsListPage with a 5-column
// Kanban view. Drag a card between columns → PUT /api/tickets/:slug { status }.
// Sky-blue palette (mirrors DashboardArchitecturePage's inline T tokens — page
// identity stays sky-blue, distinct from cream/peach editorial pages).
// Heartbeat preserved: pulsing emerald dot when in_progress, 5s auto-refresh,
// log tail under title, click-into-card opens /tickets/:slug detail.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Ticket as TicketIcon } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useApi } from "@/lib/api";
import { TicketDetailPage } from "./TicketDetailPage";

// ── Sky-blue palette (mirrors DashboardArchitecturePage T tokens) ─────────
const T = {
  bg: "#F2F7FD",
  bg2: "#E5EEF9",
  ink: "#1C1917",
  ink2: "#57534E",
  ink3: "#A8A29E",
  sky: "#7AB4F4",
  skyDark: "#3B82C8",
  skySoft: "#D2E4FC",
  line: "#D0DEF0",
  accent: "#2A6AAC",
  green: "#2A7A4B",
  greenSoft: "#E8F3EC",
  amber: "#B87333",
  amberSoft: "#FCEFD9",
  plum: "#8E4585",
  plumSoft: "#F3E6F2",
  white: "#FFFFFF",
  red: "#C0392B",
  redSoft: "#FDECEA",
};

type Status = "todo" | "in_progress" | "review" | "done" | "archived";
type Kind = "bug" | "feature" | "chore" | "task";

// A ticket wears its KIND as a chip, because a backlog that mixes "search is
// broken for the client" with "add an Excel export" is unreadable without one.
// Red for a bug is the only colour here that has to be obvious at a glance.
const KIND_STYLE: Record<Kind, { label: string; fg: string; bg: string }> = {
  bug: { label: "BUG", fg: "#C0392B", bg: "#FDECEA" },
  feature: { label: "FEATURE", fg: "#2A6AAC", bg: "#D2E4FC" },
  chore: { label: "CHORE", fg: "#57534E", bg: "#EDE9E4" },
  task: { label: "TASK", fg: "#8E4585", bg: "#F3E6F2" },
};

// Severity codes are numbered so they sort worst-first in SQL without a CASE;
// the number is an implementation detail and is stripped for display.
const SEVERITY_FG: Record<string, string> = {
  "1-critical": "#C0392B",
  "2-high": "#B87333",
  "3-medium": "#57534E",
  "4-low": "#A8A29E",
};

type TicketRow = {
  id: string;
  slug: string;
  project_id: string | null;
  project_name: string | null;
  goal_id: string | null;
  goal_title: string | null;
  agent: string | null;
  title: string;
  status: Status;
  kind: Kind;
  severity: string | null;
  project_slug: string | null;
  current_step: string | null;
  log: string | null;
  created_at: string;
  updated_at: string;
  isc_total?: number;
  isc_done?: number;
};

// THREE columns (Erez, 2026-08-11). Archived is a state, not a destination:
// "once we switch a ticket to archive it should not be visible on the screen."
// A permanent column for things you have decided to stop looking at is a column
// that only ever grows, and it was pushing DONE off the edge. Archiving now
// happens from the ticket itself — see ARCHIVE_STATUS below.
//
// REVIEW LOST ITS COLUMN TOO, and the reason is about READING, not about
// process: four columns made the block so wide that the whole section had to be
// zoomed out to fit, and then nothing in it was legible. Fewer columns is a
// narrower block, which is a bigger natural zoom.
//
// A row in `review` is NOT hidden — it is shown in In progress, keeping its
// amber card edge. Dropping the column must never drop the work; a ticket you
// cannot see is one nobody will action, and that is the same silent-swallow
// failure this project keeps finding everywhere else.
const COLUMN_ORDER: Status[] = ["todo", "in_progress", "done"];

/** Which column a status is DRAWN in. Only `review` differs from itself. */
const COLUMN_FOR: Record<Status, Status> = {
  todo: "todo",
  in_progress: "in_progress",
  review: "in_progress",
  done: "done",
  archived: "archived",
};

const STATUS_LABEL: Record<Status, string> = {
  todo: "To do",
  in_progress: "In progress",
  review: "Review",
  done: "Done",
  archived: "Archived",
};

// Column header tints — sky-blue family + status-tinted accents.
const COLUMN_HEADER_BG: Record<Status, string> = {
  todo: T.bg2,
  in_progress: T.skySoft,
  review: T.amberSoft,
  done: T.greenSoft,
  archived: T.line,
};

const COLUMN_HEADER_FG: Record<Status, string> = {
  todo: T.ink2,
  in_progress: T.skyDark,
  review: T.amber,
  done: T.green,
  archived: T.ink3,
};

// Card left-edge accent — subtle status mark.
const CARD_ACCENT: Record<Status, string> = {
  todo: T.ink3,
  in_progress: T.skyDark,
  review: T.amber,
  done: T.green,
  archived: T.line,
};

// Persona colors — sky-tinted variants where natural; existing pitch-deck
// hues otherwise.
const AGENT_COLOR: Record<string, string> = {
  jarvis: T.skyDark,
  atlas: "#C7763A",
  ben: T.green,
  nova: T.plum,
  emma: "#D6336C",
  iris: "#0EA5E9",
};

function lastLogLine(log: string | null): string | null {
  if (!log) return null;
  const lines = log
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return lines.length === 0 ? null : lines[lines.length - 1];
}

function MetaDot() {
  return (
    <span
      style={{
        color: T.ink3,
        fontSize: 14,
        userSelect: "none",
      }}
    >
      ·
    </span>
  );
}

// ── Card ────────────────────────────────────────────────────────────────────

function CardBody({
  t,
  isDragging,
  isSelected,
}: {
  t: TicketRow;
  isDragging?: boolean;
  isSelected?: boolean;
}) {
  const isLive = t.status === "in_progress";
  const tail = lastLogLine(t.log);
  const agentColor = t.agent ? AGENT_COLOR[t.agent] ?? T.ink2 : T.ink3;

  const kind = KIND_STYLE[t.kind] ?? KIND_STYLE.task;

  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${isSelected ? T.skyDark : T.line}`,
        borderLeft: `3px solid ${CARD_ACCENT[t.status]}`,
        borderRadius: 8,
        padding: "12px 14px",
        boxShadow: isDragging
          ? "0 8px 24px rgba(28,25,23,0.15)"
          : isSelected
            ? `0 0 0 2px ${T.skySoft}`
            : "0 1px 2px rgba(28,25,23,0.04)",
        opacity: isDragging ? 0.95 : 1,
        transition: "box-shadow 120ms ease, border-color 120ms ease",
      }}
    >
      {/* Kind + severity. Only a bug shows a severity, and only when it has
          one — an empty slot on every other card is noise, not information. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: kind.fg,
            background: kind.bg,
            borderRadius: 4,
            padding: "2px 6px",
          }}
        >
          {kind.label}
        </span>
        {t.severity && (
          <span
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: SEVERITY_FG[t.severity] ?? T.ink3,
            }}
            title={`Severity ${t.severity}`}
          >
            {t.severity.replace(/^\d-/, "").toUpperCase()}
          </span>
        )}
      </div>
      {/* Title row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          minWidth: 0,
          marginBottom: 8,
        }}
      >
        {isLive && (
          <span
            className="animate-pulse"
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: T.green,
              flexShrink: 0,
              marginTop: 7,
            }}
            aria-label="in progress"
            title="In progress — live heartbeat"
          />
        )}
        <h3
          style={{
            fontFamily: "Inter, sans-serif",
            // 17.5 is not a taste call — it is the SAME number as the summary
            // paragraph in the detail pane (TicketDetailPage), which Erez
            // picked by eye. A card and the pane it opens are one object seen
            // at two distances; two different body sizes read as two designs.
            fontSize: 17.5,
            fontWeight: 600,
            color: T.ink,
            margin: 0,
            lineHeight: 1.32,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
            minWidth: 0,
            flex: 1,
          }}
          title={t.title}
        >
          {t.title}
        </h3>
      </div>

      {/* Meta row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
          fontFamily: "Inter, sans-serif",
          fontSize: 13.5,
          color: T.ink2,
          lineHeight: 1.5,
        }}
      >
        <code
          style={{
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: 12.5,
            color: T.skyDark,
            background: T.skySoft,
            padding: "1px 6px",
            borderRadius: 4,
            fontWeight: 600,
          }}
        >
          {t.slug}
        </code>

        {t.agent ? (
          <>
            <MetaDot />
            <span
              style={{
                color: agentColor,
                fontWeight: 600,
                textTransform: "lowercase",
              }}
            >
              @{t.agent}
            </span>
          </>
        ) : null}

        {t.current_step ? (
          <>
            <MetaDot />
            <span
              style={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 10,
                color: isLive ? T.skyDark : T.ink3,
                fontWeight: isLive ? 600 : 400,
              }}
            >
              {t.current_step}
            </span>
          </>
        ) : null}

        {/* PROGRESS — how far along, without opening the card.
            A ticket carrying fourteen features looked identical on the board to
            one carrying none, so the board could say WHAT was being worked on
            and never HOW FAR. The bar is deliberately tiny and the number does
            the talking: a card is scanned, not studied. Absent when the ticket
            has no criteria, because an empty bar on every other card is noise
            rather than information — the same rule the severity chip follows. */}
        {t.isc_total ? (
          <>
            <MetaDot />
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
              title={`${t.isc_done ?? 0} of ${t.isc_total} criteria done`}
            >
              <span
                style={{
                  width: 34, height: 4, borderRadius: 999,
                  background: T.line, overflow: "hidden", flexShrink: 0,
                }}
              >
                <span
                  style={{
                    display: "block", height: "100%",
                    width: `${Math.round(((t.isc_done ?? 0) / t.isc_total) * 100)}%`,
                    background: (t.isc_done ?? 0) === t.isc_total ? T.green : T.skyDark,
                  }}
                />
              </span>
              <span
                style={{
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: (t.isc_done ?? 0) === t.isc_total ? T.green : T.ink3,
                }}
              >
                {t.isc_done ?? 0}/{t.isc_total}
              </span>
            </span>
          </>
        ) : null}
      </div>

      {/* Log tail */}
      {tail ? (
        <p
          style={{
            margin: "6px 0 0",
            fontFamily: "Inter, sans-serif",
            fontSize: 11,
            color: T.ink3,
            lineHeight: 1.45,
            fontStyle: "italic",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={tail}
        >
          {tail}
        </p>
      ) : null}
    </div>
  );
}

function DraggableCard({
  t,
  onOpen,
  isSelected,
}: {
  t: TicketRow;
  onOpen: (slug: string) => void;
  isSelected?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging, transform } =
    useDraggable({ id: t.id, data: { ticket: t } });

  // dnd-kit ships a `transform` we apply only when actively dragging — the
  // DragOverlay handles the floating preview, so we just hide the in-place
  // card with opacity while it's being dragged.
  const style: React.CSSProperties = {
    cursor: isDragging ? "grabbing" : "grab",
    opacity: isDragging ? 0.4 : 1,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    marginBottom: 8,
    touchAction: "none",
  };

  // Click-to-detail vs drag — PointerSensor's distance:5 activationConstraint
  // (configured at the DndContext level) decides; if the pointer moves >5px
  // before mouseup, dnd-kit takes over and onClick never fires.
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => onOpen(t.slug)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(t.slug);
        }
      }}
      aria-label={`Open ${t.slug}`}
      aria-current={isSelected ? "true" : undefined}
    >
      <CardBody t={t} isSelected={isSelected} />
    </div>
  );
}

// ── Column ──────────────────────────────────────────────────────────────────

function Column({
  status,
  selectedSlug,
  tickets,
  onOpen,
}: {
  status: Status;
  selectedSlug?: string | null;
  tickets: TicketRow[];
  onOpen: (slug: string) => void;
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: status,
    data: { status },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minWidth: 300,
        maxWidth: 360,
      }}
    >
      {/* Column header */}
      <div
        style={{
          padding: "10px 12px",
          background: COLUMN_HEADER_BG[status],
          borderRadius: "8px 8px 0 0",
          border: `1px solid ${T.line}`,
          borderBottom: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 14,
            fontWeight: 700,
            color: COLUMN_HEADER_FG[status],
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {STATUS_LABEL[status]}
        </span>
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: COLUMN_HEADER_FG[status],
            fontWeight: 600,
            background: T.white,
            padding: "1px 8px",
            borderRadius: 10,
            minWidth: 22,
            textAlign: "center",
          }}
        >
          {tickets.length}
        </span>
      </div>

      {/* Drop zone body */}
      <div
        ref={setNodeRef}
        style={{
          background: isOver ? T.skySoft : T.bg,
          border: isOver
            ? `1px dashed ${T.skyDark}`
            : `1px solid ${T.line}`,
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          padding: 10,
          // CAPPED, not open-ended (Erez, 2026-08-11: "make the height shorter,
          // by 20-25%"). The column used to grow with the card count, so eight
          // tickets set the height of the whole board section — and a taller
          // section is a section framed at a smaller zoom, which is the same
          // trap the width had, one axis over.
          //
          // THE NUMBER IS MEASURED, NOT ESTIMATED, and the first attempt is why
          // that matters: I eyeballed the block at ~767px off a screenshot and
          // capped for a 22% cut. It was actually 1196px, so that cap was a 49%
          // cut — "make it 25%", not "reduce it by 25%". Read it out of the DOM.
          //   1196 = 40 padding + 51 column header + 1105 column body
          //   1196 × 0.75 = 897  →  body 806, and the pane matches at 857.
          //
          // Scrolling here is free rather than a cost: a swipe over a column
          // that still has room scrolls the column, and the moment it bottoms
          // out the board takes over. That is the gesture rule shipped in
          // BoardCanvas, and this is the first place that leans on it.
          minHeight: 280,
          maxHeight: 806,
          overflowY: "auto",
          overscrollBehavior: "contain",
          flex: 1,
          transition: "background 120ms ease, border-color 120ms ease",
        }}
      >
        {tickets.length === 0 ? (
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              color: T.ink3,
              fontStyle: "italic",
              textAlign: "center",
              margin: "20px 0",
            }}
          >
            empty
          </p>
        ) : (
          tickets.map((t) => (
            <DraggableCard
              key={t.id}
              t={t}
              onOpen={onOpen}
              isSelected={t.slug === selectedSlug}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

/** The Kanban, as a page AND as a board section.
 *
 *  Erez, 2026-08-11: one Kanban, available to every project, filtered to one
 *  project when a board embeds it. Hence two props and no second component —
 *  a fork would have meant two drag implementations to keep in step, and the
 *  drag is the part with the optimistic-update subtlety in it.
 *
 *  @param project  project SLUG. Omit for the global board (all projects).
 *  @param embedded true when rendered inside a React Flow board: drops the
 *                  page chrome and adds the canvas opt-outs.
 */
export function TicketsKanbanPage({
  project,
  embedded = false,
}: {
  project?: string;
  embedded?: boolean;
} = {}) {
  const api = useApi();
  const navigate = useNavigate();
  const [rows, setRows] = useState<TicketRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  // EMBEDDED ONLY. On the page, clicking a card navigates to /tickets/:slug.
  // On a board there is nowhere to navigate TO without losing the canvas, so
  // the card opens beside the columns instead and the two become one app.
  const [selected, setSelected] = useState<string | null>(null);
  // Auto-select fires ONCE, on the first load. Without the latch, closing the
  // pane would be undone by the next 5s poll and the close button would look
  // broken — the user's decision has to outlive the refresh.
  const didAutoSelect = useRef(false);

  // Pending optimistic moves — { ticketId: status }. While a PUT is in flight,
  // the polling response is merged through this map so the optimistic state
  // doesn't flicker back. Cleared per-id when PUT resolves.
  const pendingMoves = useRef<Map<string, Status>>(new Map());
  const isDraggingRef = useRef(false);

  // Pointer sensor with 5px activation distance — small movement is a click
  // (opens detail), >5px is a drag. Standard dnd-kit pattern.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  useEffect(() => {
    let cancelled = false;

    async function fetchOnce() {
      // Don't clobber state mid-drag.
      if (isDraggingRef.current) return;
      try {
        const res = await api(
          project
            ? `/api/tickets?project=${encodeURIComponent(project)}`
            : "/api/tickets",
        );
        if (cancelled) return;
        if (!res.ok) {
          setError(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as TicketRow[];
        if (cancelled) return;
        // Merge: any pending optimistic move wins until its PUT resolves.
        const merged = data
          .map((row) => {
            const pending = pendingMoves.current.get(row.id);
            return pending ? { ...row, status: pending } : row;
          })
          // Dropped here rather than filtered in the render, so an archived
          // ticket is not merely hidden — it is not in the board's data and
          // cannot be counted, dragged, or re-appear on a poll.
          .filter((row) => row.status !== "archived");
        setRows(merged);
        // Open the top ticket by default (Erez, 2026-08-11) — an empty pane
        // beside a full board is a prompt to do work the app could have done.
        // `merged` is already in the API's order: status, then board_order,
        // then severity worst-first, so [0] is the thing most worth reading.
        if (embedded && !didAutoSelect.current && merged.length > 0) {
          didAutoSelect.current = true;
          setSelected(merged[0].slug);
        }
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    }

    void fetchOnce();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const handleOpen = (slug: string) => {
    if (isDraggingRef.current) return;
    if (embedded) {
      // Second click on the same card closes the pane — the columns get their
      // full width back without hunting for an X.
      setSelected((cur) => (cur === slug ? null : slug));
      return;
    }
    navigate(`/tickets/${slug}`);
  };

  /** Move a ticket to a status without a drag. Same optimistic-then-PUT path
   *  the drag uses — one way a status changes, one place it can go wrong.
   *  Used by Archive, which has no column to be dropped on any more. */
  const setStatus = async (ticket: TicketRow, newStatus: Status) => {
    const prevStatus = ticket.status;
    if (prevStatus === newStatus) return;
    pendingMoves.current.set(ticket.id, newStatus);
    setRows((cur) =>
      cur
        ? newStatus === "archived"
          ? cur.filter((r) => r.id !== ticket.id)
          : cur.map((r) => (r.id === ticket.id ? { ...r, status: newStatus } : r))
        : cur,
    );
    if (newStatus === "archived") setSelected(null);
    try {
      const res = await api(`/api/tickets/${ticket.slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      pendingMoves.current.delete(ticket.id);
    } catch (err) {
      // Put it back. An archive that silently failed would look like it
      // worked and the ticket would return on the next poll with no
      // explanation, which is the worst of both.
      pendingMoves.current.delete(ticket.id);
      setRows((cur) =>
        cur && newStatus !== "archived"
          ? cur.map((r) => (r.id === ticket.id ? { ...r, status: prevStatus } : r))
          : cur,
      );
      setError(
        err instanceof Error ? `Archive failed: ${err.message}` : "Archive failed",
      );
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    isDraggingRef.current = true;
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    isDraggingRef.current = false;
    setActiveId(null);

    const { active, over } = event;
    if (!over || !rows) return;

    const ticketId = String(active.id);
    const overData = over.data.current as { status?: Status } | undefined;
    const newStatus = overData?.status;
    if (!newStatus) return;

    const ticket = rows.find((r) => r.id === ticketId);
    if (!ticket) return;
    if (ticket.status === newStatus) return;

    const prevStatus = ticket.status;
    // Optimistic: flip locally, register pending, fire PUT.
    pendingMoves.current.set(ticketId, newStatus);
    setRows((cur) =>
      cur
        ? cur.map((r) => (r.id === ticketId ? { ...r, status: newStatus } : r))
        : cur,
    );

    try {
      const res = await api(`/api/tickets/${ticket.slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      pendingMoves.current.delete(ticketId);
    } catch (err) {
      // Revert.
      pendingMoves.current.delete(ticketId);
      setRows((cur) =>
        cur
          ? cur.map((r) =>
              r.id === ticketId ? { ...r, status: prevStatus } : r,
            )
          : cur,
      );
      setError(
        err instanceof Error
          ? `Move failed: ${err.message}`
          : "Move failed",
      );
    }
  };

  const inProgressCount =
    rows?.filter((t) => t.status === "in_progress").length ?? 0;

  const grouped: Record<Status, TicketRow[]> = {
    todo: [],
    in_progress: [],
    review: [],
    done: [],
    archived: [], // never populated — archived rows are filtered on fetch
  };
  if (rows) {
    for (const r of rows) {
      grouped[COLUMN_FOR[r.status] ?? r.status]?.push(r);
    }
  }

  const activeTicket = activeId
    ? rows?.find((r) => r.id === activeId) ?? null
    : null;

  return (
    <div
      // REACT FLOW TRAPS, when embedded in a board (same three the projects
      // kanban already hit): `nowheel` so the wheel scrolls the columns rather
      // than zooming the canvas, `nodrag nopan` so dragging a CARD does not
      // drag the whole board, and pointerEvents:auto because a non-draggable
      // RF node sets pointer-events:none on its children.
      className={embedded ? "nowheel nodrag nopan" : undefined}
      onPointerDown={embedded ? (e) => e.stopPropagation() : undefined}
      style={
        embedded
          ? {
              background: T.bg,
              borderRadius: 16,
              padding: 20,
              pointerEvents: "auto",
              // Three columns (950) + gap + the detail pane (700), ALWAYS.
              // Constant on purpose: a SectionBox hugs its content, so a width
              // that changed when the pane opened would resize the section and
              // leave the camera framing the old bounds — you would open a
              // ticket and have to go looking for it. Reserving the space
              // costs an empty panel; earning it back costs a pan every time.
              //
              // 1980 → 1710 because the section is read at whatever zoom makes
              // it FIT: every 100px of width is type the eye has to give back.
              width: 1710,
            }
          : {
              background: T.bg,
              minHeight: "100vh",
              padding: "24px 32px 80px",
            }
      }
    >
      {/* Header — the page identity. Suppressed when embedded, because the
          SectionBox label above it already says what this is, and two titles
          stacked is exactly the "no subtitles" thing the board law forbids. */}
      <div
        style={{
          display: embedded ? "none" : "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 24,
        }}
      >
        <div>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: T.skyDark,
              textTransform: "uppercase",
              margin: "0 0 6px 0",
            }}
          >
            R&amp;D · live
          </p>
          <h1
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 26,
              fontWeight: 700,
              color: T.ink,
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            Tickets
          </h1>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              color: T.ink2,
              margin: "6px 0 0",
              lineHeight: 1.55,
              maxWidth: 640,
            }}
          >
            Drag a card between columns to change status.
          </p>
        </div>

        {rows && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              fontFamily: "Inter, sans-serif",
              fontSize: 12,
            }}
          >
            {inProgressCount > 0 && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: T.green,
                  fontWeight: 600,
                }}
              >
                <span
                  className="animate-pulse"
                  style={{
                    display: "inline-block",
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: T.green,
                  }}
                />
                {inProgressCount} live
              </span>
            )}
            <span style={{ color: T.ink3 }}>
              {rows.length} ticket{rows.length === 1 ? "" : "s"}
            </span>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: "12px 16px",
            background: T.redSoft,
            border: `1px solid ${T.red}`,
            borderRadius: 10,
            color: T.red,
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          <AlertCircle
            style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
          />
          <span>{error}</span>
        </div>
      )}

      {/* Loading */}
      {!error && rows === null && (
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: T.ink3,
          }}
        >
          Loading…
        </p>
      )}

      {/* Empty */}
      {rows && rows.length === 0 && (
        <div
          style={{
            background: T.white,
            border: `1px solid ${T.line}`,
            borderRadius: 10,
            textAlign: "center",
            padding: "32px 24px",
          }}
        >
          <TicketIcon
            style={{
              width: 32,
              height: 32,
              color: T.ink3,
              margin: "0 auto 8px",
            }}
          />
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              color: T.ink,
              margin: 0,
            }}
          >
            No tickets yet
          </p>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 12,
              color: T.ink3,
              margin: "4px 0 0",
              lineHeight: 1.5,
            }}
          >
            {project
              ? "Nothing filed against this project yet."
              : "An R&D agent creates a ticket for each unit of work."}
          </p>
        </div>
      )}

      {/* Kanban board */}
      {rows && rows.length > 0 && (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            isDraggingRef.current = false;
            setActiveId(null);
          }}
        >
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "stretch",
                overflowX: "auto",
                paddingBottom: 8,
                // The columns yield width to the pane rather than the pane
                // overflowing the section — a board section cannot scroll
                // sideways the way a page can.
                flex: "0 0 950px",
                minWidth: 0,
              }}
            >
              {COLUMN_ORDER.map((status) => (
                <Column
                  key={status}
                  status={status}
                  tickets={grouped[status]}
                  onOpen={handleOpen}
                  selectedSlug={selected}
                />
              ))}
            </div>

            {/* THE DETAIL PANE. Same component the /tickets/:slug page uses —
                passing a slug instead of reading the route is the whole
                difference. `nowheel` again so the long body scrolls inside
                the pane rather than zooming the board out from under it. */}
            {embedded && !selected && (
              <div
                style={{
                  flex: "0 0 700px",
                  alignSelf: "stretch",
                  height: 857,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px dashed ${T.line}`,
                  borderRadius: 12,
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13,
                  color: T.ink3,
                }}
              >
                Select a card to read the ticket
              </div>
            )}

            {embedded && selected && (
              <div
                className="nowheel"
                onWheel={(e) => e.stopPropagation()}
                style={{
                  flex: "0 0 700px",
                  maxHeight: 857,
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                  background: T.white,
                  border: `1px solid ${T.line}`,
                  borderRadius: 12,
                  padding: "20px 24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 14,
                    marginBottom: 4,
                  }}
                >
                  <button
                    className="nopan nodrag"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      const t = rows?.find((r) => r.slug === selected);
                      if (t) void setStatus(t, "archived");
                    }}
                    title="Archive — removes it from the board"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 12,
                      color: T.ink3,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                    }}
                  >
                    archive
                  </button>
                  <button
                    className="nopan nodrag"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => setSelected(null)}
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 12,
                      color: T.ink3,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                    }}
                  >
                    close
                  </button>
                </div>
                <TicketDetailPage slug={selected} embedded />
              </div>
            )}
          </div>

          <DragOverlay>
            {activeTicket ? (
              <div style={{ width: 320, transform: "rotate(2deg)" }}>
                <CardBody t={activeTicket} isDragging />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

(TicketsKanbanPage as unknown as { path: string }).path = "/tickets";
